import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import { Avatar, Box, Checkbox, Divider, FormControlLabel, FormGroup, Grid, IconButton, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { BorderColor, Clear, Link, PhotoCamera } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { setLoading } from "../../features/authSlice";
import { getMeUser, getUser, updateUser } from "../../api/authApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonFormField from "../../common/components/CommonFormField";
import { checkAllMentorFieldsPresent, Expertise, ExpierienceYears, removeEmptyFields, serviceOptions } from "../../common/const";
import { generateMeetingLink, getMentor, updateMentorData } from "../../api/mentorApi";
import CommonSelect from "../../common/components/CommonSelect";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import ExperienceManager from "./ExperienceManager";
import { getAllSubject } from "../../api/subjectApi";
import MentorAvailability from "./MentorAvailability";

const ManageProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state) => state.auth);
  const [previewUrl, setPreviewUrl] = useState('');
  const [image, setImage] = useState(null);
  const [editPersonalProfile, setEditPersonalProfile] = useState(false);
  const [errors, setErrors] = useState({});
  const [bookingUrl, setBookingUrl] = useState(null);
  const shouldShowDropdown = (serviceValue) => {
    const servicesWithDropdowns = ['AP_TUTORING', 'ACADEMIC_TUTORING', 'CODING_SKILLS_TECHNOLOGY']; // Replace with your actual services values
    return servicesWithDropdowns.includes(serviceValue);
  };
  const [mentorData, setMentorData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    bio: '',
    expertise: [],
    experience_years: '',
    degree: '',
    service_offers: [],
    services: [],
  });
  const [experiences, setExperiences] = useState([]);
  const [dynamicOptionsMap, setDynamicOptionsMap] = useState({
    'AP_TUTORING': [],
    'ACADEMIC_TUTORING': [],
    'CODING_SKILLS_TECHNOLOGY': []
  });
  const [mentorAvailability, setMentorAvailability] = useState(null);
  const [timeZone,setTimezone] = useState(null);
  const [mentorProfileId, setMentorProfileId] = useState(null);

  const updateOptionsMapWithAPI = async () => {
    try {
      // Fetch for each tutoring type
      const tutoringTypes = ['AP_TUTORING', 'ACADEMIC_TUTORING', 'SKILL'];
      const updatedMap = { ...dynamicOptionsMap };

      for (const type of tutoringTypes) {
        const response = await getAllSubject(
          1,
          100,
          type,
          ''
        );

        const subjects = response?.data?.data || [];

        // Transform API data to options format
        updatedMap[type === 'SKILL' ? 'CODING_SKILLS_TECHNOLOGY' : type] = subjects.map(subject => ({
          value: subject.description,
          label: subject.name || subject.title
        }));
      }

      setDynamicOptionsMap(updatedMap);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      showNotification('Failed to load subjects', 'error');
    }
  };


  // Call on component mount
  useEffect(() => {
    updateOptionsMapWithAPI();
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      setImage(file);
    }
  };

  const getCurrentUser = async () => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const response = await getUser(user?.user_id);
      if (response?.data?.avatar) {
        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${response?.data.avatar}&download=false`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const blob = await responseData.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewUrl(imageUrl || '');
      }
      const mentoResponse = await getMentor(user?.user_id);
      setBookingUrl(mentoResponse?.data?.mentorProfile?.calendly_meeting_link);
      setMentorProfileId(mentoResponse?.data?.mentorProfile?.mentor_profile_id);
      setMentorAvailability(mentoResponse?.data?.mentorProfile?.availability);
      setTimezone(mentoResponse?.data?.mentorProfile?.timezone);
      setMentorData({
        first_name: user?.first_name,
        last_name: user?.last_name,
        email: user?.email,
        phone_number: mentoResponse?.data?.phone_number,
        bio: mentoResponse?.data?.mentorProfile?.bio,
        expertise: mentoResponse?.data?.mentorProfile?.expertise || [],
        experience_years: mentoResponse?.data?.mentorProfile?.experience_years,
        degree: mentoResponse?.data?.mentorProfile?.degree,
        service_offers: mentoResponse?.data?.mentorProfile?.service_offers,
        services: mentoResponse?.data?.mentorProfile?.mentorServices || []
      });
      setExperiences(mentoResponse?.data?.mentorProfile?.experiences || []);
      dispatch(setLoading({
        loading: false
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      showNotification(errorMessage, 'error');
      dispatch(setLoading({
        loading: false
      }));
    }
  }

  const validateField = (name, value) => {
    switch (name) {
      case 'first_name':
        if (!value) return 'First Name is required';
        return '';
      case 'last_name':
        if (!value) return 'Last Name is required';
        return '';
      case 'phone_number':
        if (!value) return 'Phone Number is required';
        if (value.includes('+91') ? value.length < 12 : value.length < 11) return 'Please enter valid phone number';
        return '';
      case 'bio':
        if (!value) return 'Bio is required';
        if (value.length > 500) return 'Bio contains maximum 500 character';
        return '';
      case 'expertise':
        if (value.length === 0) return 'Please select expertise';
        return '';
      case 'experience_years':
        if (!value) return 'Please select experience years';
        return '';
      case 'degree':
        if (!value) return 'Educational Degree is required';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMentorData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for the field being changed
    if (errors[name]) {
      const error = validateField(name, value);
      if (!error) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      } else {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(mentorData).forEach(key => {
      const error = validateField(key, mentorData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    try {
      e.preventDefault();
      if (!validateForm()) return;
      if (mentorData.service_offers.length > 0) {
        dispatch(setLoading({
          loading: true
        }));
        const formdata = new FormData();
        if (image) {
          formdata.append('avatar', image);
        } else if (previewUrl === '') {
          formdata.append('avatar', null);
        }
        const response = await updateUser(user?.user_id, formdata);
        if (response?.data?.user_id) {
          dispatch(setLoading({
            loading: true
          }));
          mentorData.experiences = experiences;
          const res = await updateMentorData(user?.user_id, removeEmptyFields(mentorData));
          if (res.success) {
            showNotification(res.message, 'success');
            dispatch(setLoading({
              loading: false
            }));
            getCurrentUser();
            setImage(null);
            setEditPersonalProfile(false);
            dispatch(setLoading({
              loading: false
            }));
            const userResponse = await getMeUser();
            if (userResponse.data?.user_id) {
              if (checkAllMentorFieldsPresent(userResponse.data)) {
                navigate('/mentor-dashboard');
              }
            }
          }
        }
      } else {
        showNotification('Please select services offered', 'error');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      dispatch(setLoading({
        loading: false
      }));
      showNotification(errorMessage, 'error');
    }
  };

  useEffect(() => {
    if (user?.user_id) {
      getCurrentUser();
    }
  }, [user?.user_id]);

  const getServiceDetails = (serviceType) => {
    const services = mentorData.services?.find(
      detail => detail.service_name === serviceType
    );
    return services ? services.sub_service : [];
  };

  // Handle dropdown changes
  const handleServiceDetailsChange = (serviceType, selections) => {
    setMentorData(prev => {
      const existingServices = prev.services || [];

      // Find if this services already exists
      const existingIndex = existingServices.findIndex(
        item => item.service_name === serviceType
      );

      let updatedServices;

      if (selections.length > 0) {
        // Create new services object
        const newService = {
          service_name: serviceType,
          sub_service: selections
        };

        if (existingIndex >= 0) {
          // Replace existing services
          updatedServices = [...existingServices];
          updatedServices[existingIndex] = newService;
        } else {
          // Add new services
          updatedServices = [...existingServices, newService];
        }
      } else {
        // Remove services if selections are empty
        updatedServices = existingServices.filter(
          item => item.service_name !== serviceType
        );
      }

      return {
        ...prev,
        services: updatedServices
      };
    });
  };

  // Update checkbox handler to clean up service_details when unchecked
  const handleCheckboxChange = (services, checked) => {
    if (checked) {
      setMentorData((prev) => ({
        ...prev,
        service_offers: [...(prev.service_offers || []), services.value]
      }));
    } else {
      setMentorData((prev) => ({
        ...prev,
        service_offers: (prev.service_offers || []).filter((a) => a !== services.value),
        services: (prev.service_details || []).filter(
          detail => !detail.hasOwnProperty(services.value)
        )
      }));
    }
  };


  return (
    <Fragment>
      {loading && (
        <LoadingSpinner />
      )}
      <CommonCard
        borderRadius={1}
        border={`1px solid ${theme.palette.background.secondary}`}
        paddingBottom="0px"
        padding="0px"
        marginTop="0px"
        headerContent={
          <Fragment>
            <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontSize={22} fontWeight={600}>Manage Personal Details</Typography>
                {bookingUrl && (
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(bookingUrl);
                      showNotification('Copy successfully', 'success');
                    }}
                  >
                    <Link sx={{ color: theme.palette.secondary.main }} />
                  </IconButton>
                )}
              </Box>
              {!editPersonalProfile && (
                <CommonButton
                  label="Edit"
                  handleClick={() => setEditPersonalProfile(true)}
                  marginBottom="10px"
                  marginTop="10px"
                  fontWeight={600}
                  startIcon={<BorderColor />}
                  width='100px'
                />
              )}
            </Box>
            <Divider />
          </Fragment>
        }
        content={
          <Fragment>
            <Grid container spacing={2} sx={{ padding: '13px', marginBottom: 1 }}>
              <Grid item xs={12} md={3}>
                <Grid container spacing={2}>
                  <Grid item md={4} xs={12}>
                    <Box sx={{ position: 'relative', mb: 1, display: 'inline-block' }}>
                      <Avatar
                        src={previewUrl}
                        sx={{
                          width: 100,
                          height: 100,
                          backgroundColor: theme.palette.grey[200],
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          if (editPersonalProfile) {
                            document.getElementById('profile-image-upload').click()
                          }
                        }}
                      >
                        <PhotoCamera sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
                      </Avatar>
                      <input
                        type="file"
                        id="profile-image-upload"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />

                      {previewUrl && editPersonalProfile && (
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: 'error.main',
                            color: 'white',
                            width: 24,
                            height: 24,
                            '&:hover': {
                              backgroundColor: 'error.dark',
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewUrl('');
                            setImage(null);
                          }}
                        >
                          <Clear sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    {!bookingUrl && (
                      <CommonButton
                        label="Generate Booking Url"
                        fontWeight={600}
                        height="35px"
                        width="180px"
                        handleClick={async () => {
                          try {
                            dispatch(setLoading({
                              loading: true
                            }));
                            const response = await generateMeetingLink();
                            if (response.success) {
                              showNotification(response.message, 'success');
                              dispatch(setLoading({
                                loading: false
                              }));
                              getCurrentUser();
                            }
                          } catch (err) {
                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                            dispatch(setLoading({
                              loading: false
                            }));
                            showNotification(errorMessage, 'error');
                          }
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={9} xs={12}>
                <Grid container spacing={2}>
                  <Grid item sm={6}>
                    <CommonFormField
                      label="First Name *"
                      type="email"
                      name="first_name"
                      disabled={!editPersonalProfile}
                      value={mentorData.first_name}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      error={errors.first_name}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonFormField
                      label="Last Name *"
                      type="email"
                      name="last_name"
                      disabled={!editPersonalProfile}
                      value={mentorData.last_name}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      error={errors.last_name}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonFormField
                      label="Email *"
                      type="email"
                      name="email"
                      disabled
                      value={mentorData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email"
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonFormField
                      label="Phone Number"
                      name="phone"
                      value={mentorData.phone_number}
                      disabled={!editPersonalProfile}
                      onChange={(e) => {
                        setMentorData((prev) => ({
                          ...prev,
                          phone_number: e?.phone,
                        }));
                        if (errors['phone_number']) {
                          const error = validateField('phone_number', e?.phone);
                          if (!error) {
                            setErrors(prev => ({ ...prev, phone: undefined }));
                          } else {
                            setErrors(prev => ({ ...prev, phone: error }));
                          }
                        }
                      }}
                      placeholder="Enter phone number"
                      error={errors.phone_number}
                      isPhone={true}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonSelect
                      label="Expertise"
                      name="expertise"
                      disabled={!editPersonalProfile}
                      value={mentorData.expertise}
                      onChange={handleInputChange}
                      multiple={true}
                      placeholder="Enter Experties Detail"
                      options={Expertise}
                      error={errors.expertise}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonSelect
                      label="Experience (Year)"
                      name="experience_years"
                      disabled={!editPersonalProfile}
                      value={mentorData.experience_years}
                      onChange={handleInputChange}
                      placeholder="Select Experience"
                      options={ExpierienceYears}
                      error={errors.experience_years}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonFormField
                      label="Educational Degree"
                      type="email"
                      disabled={!editPersonalProfile}
                      name="degree"
                      value={mentorData.degree}
                      onChange={handleInputChange}
                      placeholder="Enter Educational Degree"
                      error={errors.degree}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <CommonFormField
                      label="Bio"
                      type="email"
                      name="bio"
                      multiline
                      disabled={!editPersonalProfile}
                      value={mentorData.bio}
                      onChange={handleInputChange}
                      placeholder="Enter Bio Detail"
                      error={errors.bio}
                    />
                  </Grid>
                  <ExperienceManager
                    experiences={experiences}
                    setExperiences={setExperiences}
                    editPersonalProfile={editPersonalProfile}
                  />
                  <Grid item sm={12}>
                    <Typography color={theme.palette.background.dark}>Services Offered</Typography>
                    <FormGroup>
                      {serviceOptions.map((services) => (
                        <Box key={services.value}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={!editPersonalProfile}
                                value={services.value}
                                checked={mentorData.service_offers?.includes(services.value)}
                                onChange={(e) => handleCheckboxChange(services, e.target.checked)}
                              />
                            }
                            label={services.label}
                          />
                          {mentorData.service_offers?.includes(services.value) &&
                            shouldShowDropdown(services.value) && (
                              <CommonSelect
                                name={`${services.label} Details`}
                                multiple
                                disabled={!editPersonalProfile}
                                value={getServiceDetails(services.value)}
                                onChange={(e) => handleServiceDetailsChange(services.value, e.target.value)}
                                placeholder={`Select ${services.value === 'AP_TUTORING' ? 'AP Subject' :
                                  services.value === 'ACADEMIC_TUTORING' ? 'Academic Subject' :
                                    'Tech Stack'
                                  }`}
                                options={dynamicOptionsMap[services.value]}
                              />
                            )}
                        </Box>
                      ))}
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      width="100%"
                      paddingRight="5px"
                    >
                      <CommonButton
                        label="Save Changes"
                        handleClick={handleProfileSubmit}
                        marginBottom="10px"
                        marginTop="10px"
                        fontWeight={600}
                        width='140px'
                        disabled={!editPersonalProfile}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <MentorAvailability 
                      editPersonalProfile={editPersonalProfile}
                      mentorAvailability={mentorAvailability}
                      timeZone={timeZone}
                      getCurrentUser={getCurrentUser}
                      mentorProfileId={mentorProfileId}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Fragment>
        }
      />
    </Fragment>
  )
}

export default ManageProfile